import { Badge } from "@components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@components/ui/popover";
import { UserProfile } from "@ui/internal/components/card";
import { useHandleAction } from "@utils/hook/index.hook";
import { ColumnType } from "@utils/interface/index.interface";
import { InvoiceData } from "@utils/interface/journey.interface";
import { EllipsisVertical, Eye, Send, Trash2 } from "lucide-react";
import { useMemo } from "react";

const ColumnList = () => {
  const { handleAction } = useHandleAction({});

  const handleInvoice = (data: number) => {
    handleAction({ type: "mode-add", data: { type: "email", id: data } });
  };

  const column: ColumnType<InvoiceData>[] = useMemo(
    () => [
      {
        name: "ID",
        accessKey: "id",
        cell: (row) => (
          <span className="font-semibold text-gray-700">{row.id}</span>
        ),
        sortable: true,
      },
      {
        name: "Date",
        accessKey: "created_date",
        cell: (row) => (
          <span className="font-semibold text-gray-700">
            {row.created_date}
          </span>
        ),
        sortable: true,
      },
      {
        name: "Invoice No.",
        accessKey: "invoice_no",
        cell: (row) => (
          <span className="font-semibold text-gray-700">{row.invoice_no}</span>
        ),
        sortable: true,
      },
      {
        name: "Student Name",
        accessKey: "student__user__first_name",
        cell: (row) => (
          <UserProfile
            userdesignation={row.student.user.email}
            userimage={row.student.user.professional_image}
            username={row.student.user.full_name}
          />
        ),
        sortable: true,
      },
      {
        name: "Course Name",
        accessKey: "course_batch__name",
        cell: (row) => (
          <>
            <Badge variant={"warning"}>{row.course_batch.name}</Badge>
          </>
        ),
        sortable: false,
      },
      {
        name: "Total Amount",
        accessKey: "total_amount",
        cell: (row) => (
          <div className="text-sm font-medium text-gray-800">
            {row.total_amount}
          </div>
        ),
        sortable: true,
      },
      {
        name: "Course type",
        cell: (row) => (
          <div>
            {row.is_see && (
              <span className="bg-amber-500 px-2 py-[2px] rounded-full">
                SEE
              </span>
            )}
            {row.is_plus2 && (
              <span className="bg-amber-500 px-2 py-[2px] rounded-full">
                Plus 2
              </span>
            )}
            {row.is_masterclass && (
              <span className="bg-amber-500 px-2 py-[2px] rounded-full">
                Master Class
              </span>
            )}
            {!row.is_see && !row.is_plus2 && !row.is_masterclass && (
              <span className="bg-amber-500 px-2 py-[2px] rounded-full">
                Normal
              </span>
            )}
          </div>
        ),
        sortable: false,
      },
      {
        accessKey: "due_amount",
        name: "Due Amount",
        cell: (row) => (
          <div className="text-sm font-medium text-gray-800">
            {row.due_amount}
          </div>
        ),
        sortable: true,
      },
      {
        name: "Email Status",
        cell: (row) => (
          <div className="text-sm font-medium text-gray-800">
            {row?.status.replace("_", " ")}
          </div>
        ),
        sortable: true,
      },
      {
        name: "Actions",
        cell: (row) => (
          <>
            <Popover>
              <PopoverTrigger>
                <EllipsisVertical className="cursor-pointer" size={16} />
              </PopoverTrigger>
              <PopoverContent>
                <ul className="list-unstyled space-y-2">
                  <li
                    onClick={() =>
                      handleAction({
                        type: "page-edit",
                        data: row.id,
                      })
                    }
                    className="text-[14px] flex gap-2 items-center cursor-pointer"
                  >
                    <Eye size={16} /> View / Edit Invoice
                  </li>
                  <li
                    onClick={() => handleInvoice(row.id)}
                    className="text-[14px] flex gap-2 items-center cursor-pointer"
                  >
                    <Send size={16} /> Send Invoice
                  </li>
                  {row?.latest_invoice && (
                    <li
                      onClick={() =>
                        handleAction({
                          type: "confirm",
                          data: { type: "delete", id: row.id },
                        })
                      }
                      title="Delete"
                      className="text-[14px] flex gap-2 items-center cursor-pointer text-red-500"
                    >
                      <Trash2 size={16} /> Delete
                    </li>
                  )}
                </ul>
              </PopoverContent>
            </Popover>
          </>
        ),
        sortable: false,
      },
    ],
    [handleAction]
  );
  return column;
};

export { ColumnList };
